<template>
  <div>
    <v-snackbar v-model="error.show">
      <p class="py-0 my-0 font-weight-black">{{ error.title }}</p>
      {{ error.message }}

      <template v-slot:action="{ attrs }">
        <v-btn color="blue" text v-bind="attrs" @click="error.show = false">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>

    <v-navigation-drawer v-model="drawer" :mini-variant="mini" app color="white" permanent class="sidebar-custom">
      <v-list-item class="px-2 py-4" style="border-bottom: 2px solid #1976D2;">
        <v-list-item-avatar v-if="!mini" color="blue" class="mr-2">
          <v-icon color="white">mdi-account-circle-outline</v-icon>
        </v-list-item-avatar>

        <v-list-item-title v-if="!mini" class="text-h6 blue--text text--darken-2 font-weight-bold">
          Painel
        </v-list-item-title>

        <v-btn icon @click.stop="mini = !mini" color="blue">
          <v-icon>{{ mini ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
        </v-btn>
      </v-list-item>

      <v-list dense nav class="pt-2">
        <!-- Início -->
        <v-tooltip right :disabled="!mini">
          <template v-slot:activator="{ on, attrs }">
            <v-list-item link to="/inicio" color="grey" v-on="on" v-bind="attrs">
              <v-list-item-icon>
                <v-icon color="grey">mdi-home</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="grey--text">Início</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <span>Início</span>
        </v-tooltip>

        <!-- Dashboard -->
        <v-tooltip right :disabled="!mini" v-if="isAdmin">
          <template v-slot:activator="{ on, attrs }">
            <v-list-item link to="/dashboard" color="grey" v-on="on" v-bind="attrs">
              <v-list-item-icon>
                <v-icon color="grey">mdi-view-dashboard</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="grey--text">Dashboard</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <span>Dashboard</span>
        </v-tooltip>

        <!-- Clientes -->
        <v-tooltip right :disabled="!mini">
          <template v-slot:activator="{ on, attrs }">
            <v-list-item link to="/lista" color="grey" v-on="on" v-bind="attrs">
              <v-list-item-icon>
                <v-icon color="grey">mdi-account-group</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="grey--text">Clientes</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <span>Clientes</span>
        </v-tooltip>

        <!-- Tickets -->
        <v-tooltip right :disabled="!mini">
          <template v-slot:activator="{ on, attrs }">
            <v-list-item link to="/tickets" :class="{ 'v-list-item--active': isTicketsActive }" v-on="on" v-bind="attrs">
              <v-list-item-icon>
                <v-icon :color="isTicketsActive ? '#1976D2' : 'grey'">mdi-ticket-account</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title
                  :class="isTicketsActive ? 'blue--text text--darken-2' : 'grey--text'">Tickets</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <span>Tickets</span>
        </v-tooltip>

        <!-- Financeiro -->
        <v-tooltip right :disabled="!mini" v-if="isAdmin">
          <template v-slot:activator="{ on, attrs }">
            <span v-on="on" v-bind="attrs">
              <v-list-group prepend-icon="mdi-cash-multiple" color="grey" no-action>
                <template v-slot:activator>
                  <v-list-item-title class="grey--text">Financeiro</v-list-item-title>
                </template>

                <v-list-item link @click="irParaFaturas()" class="pl-8"
                  :class="{ 'v-list-item--active': isFinanceiroActive }">
                  <v-list-item-icon>
                    <v-icon :color="isFinanceiroActive ? '#1976D2' : 'grey'">mdi-invoice-list-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title
                    :class="isFinanceiroActive ? 'blue--text text--darken-2' : 'grey--text'">Faturas</v-list-item-title>
                </v-list-item>

                <v-list-item link to="/faturamento/" class="pl-8" :class="{ 'v-list-item--active': isFaturamentoActive }">
                  <v-list-item-icon>
                    <v-icon :color="isFaturamentoActive ? '#1976D2' : 'grey'">mdi-invoice-clock-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title
                    :class="isFaturamentoActive ? 'blue--text text--darken-2' : 'grey--text'">Faturamento</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="abrirTabelaValores()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-list-box-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Tabela de valores</v-list-item-title>
                </v-list-item>

                <v-list-item link to="/calculadora/" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-calculator-variant</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Calculadora</v-list-item-title>
                </v-list-item>
              </v-list-group>
            </span>
          </template>
          <span>Financeiro</span>
        </v-tooltip>

        <!-- Integrações -->
        <v-tooltip right :disabled="!mini" v-if="isAdmin">
          <template v-slot:activator="{ on, attrs }">
            <span v-on="on" v-bind="attrs">
              <v-list-group prepend-icon="mdi-connection" color="grey" no-action>
                <template v-slot:activator>
                  <v-list-item-title class="grey--text">Integrações</v-list-item-title>
                </template>

                <v-list-item link @click="verificarWhatsApp()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-whatsapp</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">WhatsApp</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="carregarAsaas()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-cash-clock</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Asaas</v-list-item-title>
                </v-list-item>
              </v-list-group>
            </span>
          </template>
          <span>Integrações</span>
        </v-tooltip>

        <!-- Links úteis -->
        <v-tooltip right :disabled="!mini">
          <template v-slot:activator="{ on, attrs }">
            <span v-on="on" v-bind="attrs">
              <v-list-group prepend-icon="mdi-link-variant" color="grey" no-action>
                <template v-slot:activator>
                  <v-list-item-title class="grey--text">Links úteis</v-list-item-title>
                </template>

                <v-list-item link @click="irParaGuia()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-cast-education</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Guia rápido</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="irParaApoio()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-handshake</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Apoio ao parceiro</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="irParaApoioSuporte()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-face-agent</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Apoio ao suporte</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="irParaYoutube()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-youtube-tv</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Biblioteca de treinamentos</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="irParaAjuda()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-help-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Central de ajuda</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="irParaStatus()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-server-network</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Status</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="irParaSugestoes()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-gesture-tap-button</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Feedback</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="irParaComunidade()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-whatsapp</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Comunidade</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="irParaDoc()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-api</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">API</v-list-item-title>
                </v-list-item>

              </v-list-group>
            </span>
          </template>
          <span>Links úteis</span>
        </v-tooltip>

        <!-- Downloads -->
        <v-tooltip right :disabled="!mini">
          <template v-slot:activator="{ on, attrs }">
            <span v-on="on" v-bind="attrs">
              <v-list-group prepend-icon="mdi-download" color="grey" no-action>
                <template v-slot:activator>
                  <v-list-item-title class="grey--text">Downloads</v-list-item-title>
                </template>

                <v-list-item link @click="baixar('https://painel.gamadelivery.app/uteis/g-instalador.exe')" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-package-down</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">G-Instalador</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="baixar('https://painel.gamadelivery.app/uteis/g-balanca.exe')" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-scale-balance</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">G-Balança</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="baixar('https://painel.gamadelivery.app/pdv/gpdv.exe')" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-cash-register</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">G-PDV</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="baixar('https://painel.gamadelivery.app/uteis/tef.zip')" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-credit-card-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">G-TEF</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="baixar('https://painel.gamadelivery.app/totem/totem.apk')" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-tablet-dashboard</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">G-Totem</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="baixar('https://painel.gamadelivery.app/totem/tablet.apk')" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-tablet</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">G-Mesas</v-list-item-title>
                </v-list-item>
              </v-list-group>

            </span>
          </template>
          <span>Downloads</span>
        </v-tooltip>

        <!-- Gerenciamento -->
        <v-tooltip right :disabled="!mini">
          <template v-slot:activator="{ on, attrs }">
            <span v-on="on" v-bind="attrs">
              <v-list-group prepend-icon="mdi-cog-outline" color="grey" no-action v-if="!isRevenda || isDesenvolvimento">
                <template v-slot:activator>
                  <v-list-item-title class="grey--text">Gerenciamento</v-list-item-title>
                </template>

                <v-list-item link @click="carregarInstalacoes()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-laptop</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Instalações</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="carregarRevendas()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-store</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Revendas</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="carregarTransacoes()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-bank-transfer</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Transações</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="carregarInstanciasWhatsApp()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-whatsapp</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Instâncias</v-list-item-title>
                </v-list-item>

                <v-list-item link @click="abrirDesbloquearIp()" class="pl-8">
                  <v-list-item-icon>
                    <v-icon color="grey">mdi-lock-open-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="grey--text">Desbloquear IP</v-list-item-title>
                </v-list-item>
              </v-list-group>
            </span>
          </template>
          <span>Gerenciamento</span>
        </v-tooltip>

        <v-divider class="my-2"></v-divider>

        <!-- Ajustes -->
        <v-tooltip right :disabled="!mini" v-if="isAdmin">
          <template v-slot:activator="{ on, attrs }">
            <v-list-item link @click="abrirAjustes()" color="grey" v-on="on" v-bind="attrs">
              <v-list-item-icon>
                <v-icon color="grey">mdi-cog</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="grey--text">Ajustes</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <span>Ajustes</span>
        </v-tooltip>

        <!-- Sair -->
        <v-tooltip right :disabled="!mini">
          <template v-slot:activator="{ on, attrs }">
            <v-list-item link @click="sair()" v-on="on" v-bind="attrs">
              <v-list-item-icon>
                <v-icon>mdi-logout</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="grey--text">Sair</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <span>Sair</span>
        </v-tooltip>
      </v-list>

      <template v-slot:append>
        <div class="pa-2" v-if="isAdmin">
          <v-tooltip top max-width="260">
            <template v-slot:activator="{ on, attrs }">
              <v-card @click="carregarRevenda()" class="credit-card" elevation="2" hover v-bind="attrs" v-on="on">
                <v-card-text class="d-flex align-center pa-3">
                  <v-icon :color="mostrarCreditos ? 'blue' : 'grey'" class="mr-3">
                    {{ mostrarCreditos ? 'mdi-eye' : 'mdi-eye-off' }}
                  </v-icon>
                  <div v-if="!mini" class="flex-grow-1">
                    <div class="caption grey--text">Créditos</div>
                    <div class="font-weight-bold" :class="mostrarCreditos ? 'blue--text' : ''">
                      {{ mostrarCreditos ? formatarMoeda(revenda.creditos) : '••••' }}
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </template>
            <span>Sabia que ao adicionar créditos antecipadamente você ganha 10% de desconto?</span>
          </v-tooltip>
        </div>
      </template>

      <!-- Diálogo Sair -->
      <v-dialog v-model="dialogSair" max-width="420px">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-logout</v-icon>
            <span class="headline">Sair</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogSair = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-6">
            <p class="text-body-1 mb-0">Tem certeza que deseja sair do painel?</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="dialogSair = false">
              Cancelar
            </v-btn>
            <v-btn color="primary" text @click="confirmarSair()">
              <v-icon left>mdi-logout</v-icon>
              Sair
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo WhatsApp -->
      <v-dialog v-model="dialogWhatsApp" max-width="600px">
        <v-card>
          <v-card-title class="success white--text">
            <v-icon left color="white">mdi-whatsapp</v-icon>
            <span class="headline">WhatsApp</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogWhatsApp = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text style="max-height: 70vh; overflow-y: auto;">
            <p class="mt-2">Conecte-se no WhatsApp para enviar notificações automáticas aos seus clientes.</p>

            <v-container class="text-center">
              <img :src="qrcode" width="300" height="300" />
            </v-container>

            <p>Aponte a câmera do seu celular em até 1 minuto, caso contrário será necessário gerar outro QR Code.</p>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialogWhatsApp = false">
              Fechar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo Asaas -->
      <v-dialog v-model="dialogAsaas" max-width="600px">
        <v-card>
          <v-card-title class="warning white--text">
            <v-icon left color="white">mdi-cash-clock</v-icon>
            <span class="headline">Asaas</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogAsaas = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text style="max-height: 70vh; overflow-y: auto;">
            <p class="mt-2">Deixe tudo no automático, automatize a cobrança dos seus clientes com Asaas de maneira fácil e
              eficaz.</p>

            <v-container>
              <h3 class="mt-3 blue--text">Informações</h3>
              <hr class="mb-3 mt-2" />

              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field class="mb-2" v-model="webhookUrl" label="URL do Webhook"
                    hint="É uma url específica do painel para recebimento dos eventos" type="text" filled readonly
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <h3 class="mt-3 blue--text">Tutoriais</h3>
              <hr class="mb-3 mt-2" />

              <p class="mt-3">Passo 1) Deixe tudo no automático ativando o webhook no Asaas de maneira fácil e eficaz.</p>
              <div style="padding:56% 0 0 0;position:relative;">
                <iframe
                  src="https://player.vimeo.com/video/1045382913?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&autoplay=1&title=0&sidedock=0"
                  frameborder="0" allow="fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  style="position:absolute;top:0;left:0;width:100%;height:100%;"
                  title="Automatização das assinaturas com Asaas"></iframe>
              </div>

              <p class="mt-3">Passo 2) Agora precisamos gerar o seu token para alterar aqui no painel.</p>
              <div style="padding:56% 0 0 0;position:relative;">
                <iframe
                  src="https://player.vimeo.com/video/1060614793?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&title=0&sidedock=0"
                  frameborder="0" allow="fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  style="position:absolute;top:0;left:0;width:100%;height:100%;"
                  title="Automatização das assinaturas com Asaas"></iframe>
              </div>

              <p class="mt-5">Passo 3) Após a ativação, basta cadastrar as assinaturas dos seus clientes e o Asaas irá
                automatizar a cobrança.</p>
              <div style="padding:46% 0 0 0;position:relative;">
                <iframe
                  src="https://player.vimeo.com/video/1053042863?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&title=0&sidedock=0"
                  frameborder="0" allow="fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  style="position:absolute;top:0;left:0;width:100%;height:100%;"
                  title="Automatização das assinaturas com Asaas"></iframe>
              </div>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialogAsaas = false">
              Fechar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo Ajustes -->
      <v-dialog v-model="dialogAjustes" max-width="600px">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-cog</v-icon>
            <span class="headline">Ajustes</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogAjustes = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text style="max-height: 70vh; overflow-y: auto;">
            <v-container>
              <h3 class="mt-3 blue--text">Informações do responsável</h3>
              <hr class="mb-3 mt-2" />

              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field class="mb-2" v-model="inputs.nome_empresa" label="Empresa"
                    hint="Nome da empresa ou responsável" type="text" filled required persistent-hint></v-text-field>

                  <v-text-field class="mb-2" v-model="inputs.nome_sistema" label="Sistema" hint="Nome do sistema"
                    type="text" filled required persistent-hint></v-text-field>

                  <v-text-field class="mb-2" v-model="inputs.email" label="Email" hint="Email de contato" type="email"
                    filled required persistent-hint></v-text-field>

                  <v-text-field class="mb-2" v-model="inputs.telefone" label="Telefone" hint="Telefone de contato"
                    type="text" filled required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <h3 class="mt-3 blue--text">Identificações</h3>
              <hr class="mb-3 mt-2" />

              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field class="mb-2" v-model="inputs.ajuda" label="Ajuda" hint="Link de ajuda para os clientes"
                    type="text" filled required persistent-hint></v-text-field>

                  <v-text-field class="mb-2" v-model="inputs.jivochat" label="Jivochat"
                    hint="Código de identificação do chat" type="text" filled required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <h3 class="mt-3 blue--text">Limites</h3>
              <hr class="mb-3 mt-2" />

              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field class="mb-2" v-model="inputs.limite_emissao_fiscal_mes" label="Emissão fiscal - NFC-e"
                    hint="Limite de emissões fiscais de NFC-e por mês" type="number" filled required
                    persistent-hint></v-text-field>

                  <v-text-field class="mb-2" v-model="inputs.limite_emissao_fiscal_nfe_mes" label="Emissão fiscal - NF-e"
                    hint="Limite de emissões fiscais de NF-e por mês" type="number" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <h3 class="mt-3 blue--text">Tokens da {{ inputs.nome_empresa }}</h3>
              <hr class="mb-3 mt-2" />

              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field class="mb-2" v-model="inputs.asaas_token" label="Token Asaas"
                    hint="Token secreto da plataforma do Asaas" type="text" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <h3 class="mt-3 blue--text">E-mail (SMTP)</h3>
              <hr class="mb-3 mt-2" />

              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-switch v-model="inputs.smtp_autenticacao" label="Autenticação" :true-value="true"
                    :false-value="false"></v-switch>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field class="mb-2" v-model="inputs.smtp_usuario" label="Usuário"
                    hint="Usuário para autenticação" type="text" filled required persistent-hint></v-text-field>

                  <v-text-field class="mb-2" v-model="inputs.smtp_email" label="E-mail" hint="E-mail para autenticação"
                    type="email" filled required persistent-hint></v-text-field>

                  <v-text-field class="mb-2" v-model="inputs.smtp_senha" label="Senha" hint="Senha para autenticação"
                    type="password" filled required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field class="mb-2" label="Protocolo" hint="SMTP" type="text" value="tls" filled readonly
                    persistent-hint></v-text-field>

                  <v-text-field class="mb-2" v-model="inputs.smtp_host" label="Host" hint="SMTP" type="text" filled
                    required persistent-hint></v-text-field>

                  <v-text-field class="mb-2" v-model="inputs.smtp_porta" label="Porta" hint="SMTP" type="number" filled
                    required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <h3 class="mt-3 blue--text">Precificação dos planos</h3>
              <hr class="mb-3 mt-2" />

              <div v-for="(plano, index) in planos" :key="index">
                <v-row>
                  <v-col cols="12">
                    <v-text-field v-model="inputs[`preco_plano_${plano.value}`]" :label="`Plano ${plano.text}`"
                      hint="Defina o preço para mensalidade do plano" type="text" prefix="R$" filled required
                      persistent-hint></v-text-field>
                  </v-col>
                </v-row>
              </div>

              <h3 class="mt-3 blue--text">Precificação adicional</h3>
              <hr class="mb-3 mt-2" />

              <!-- PDV -->
              <v-row>
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_pdv" :items="planos" label="Plano - PDV" hint="Selecione o plano"
                    filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_pdv" label="Preço - PDV" hint="Defina o preço para venda"
                    type="text" prefix="R$" filled required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Mesas -->
              <v-row>
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_mesas" :items="planos" label="Plano - Mesas"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_mesas" label="Preço - Mesas" hint="Defina o preço para venda"
                    type="text" prefix="R$" filled required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Reservas -->
              <v-row>
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_reservas" :items="planos" label="Plano - Reservas"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_reservas" label="Preço - Reservas"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Fila -->
              <v-row>
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_fila" :items="planos" label="Plano - Fila Online"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_fila" label="Preço - Fila Online"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- App Motoboy -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_app_motoboy" :items="planos" label="Plano - G-Entregas"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_app_motoboy" label="Preço - G-Entregas"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Monitor de Senhas -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_senhas" :items="planos" label="Plano - Monitor de Senhas"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_senhas" label="Preço - Monitor de Senhas"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Monitor de Preparos -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_preparo" :items="planos" label="Plano - Monitor de Preparos"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_preparo" label="Preço - Monitor de Preparos"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Monitor de Balanças -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_balanca" :items="planos" label="Plano - Monitor de Balanças"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_balanca" label="Preço - Monitor de Balanças"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Monitor de Entregas -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_entregas" :items="planos" label="Plano - Monitor de Entregas"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_entregas" label="Preço - Monitor de Entregas"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Monitor do Entregador -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_entregador" :items="planos" label="Plano - Monitor do Entregador"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_entregador" label="Preço - Monitor do Entregador"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Financeiro -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_financeiro" :items="planos" label="Plano - Financeiro"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_financeiro" label="Preço - Financeiro"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Controle de Estoque -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_estoque" :items="planos" label="Plano - Controle de Estoque"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_estoque" label="Preço - Controle de Estoque"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Integrações -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_integracoes" :items="planos" label="Plano - Integrações"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_integracoes" label="Preço - Integrações"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Fidelização de clientes -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_marketing" :items="planos"
                    label="Plano - Fidelização de clientes" hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_marketing" label="Preço - Fidelização de clientes"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Campanhas -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_campanhas" :items="planos" label="Plano - Campanhas"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_campanhas" label="Preço - Campanhas"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Campanhas por SMS -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_marketing_sms" :items="planos" label="Plano - Campanhas por SMS"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_marketing_sms" label="Preço - Campanhas por SMS"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Campanhas por WhatsApp -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_marketing_whatsapp_marketing" :items="planos"
                    label="Plano - Campanhas por WhatsApp" hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_marketing_whatsapp_marketing"
                    label="Preço - Campanhas por WhatsApp" hint="Defina o preço para venda" type="text" prefix="R$" filled
                    required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Campanhas por E-mail -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_marketing_email_marketing" :items="planos"
                    label="Plano - Campanhas por E-mail" hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_marketing_email_marketing"
                    label="Preço - Campanhas por E-mail" hint="Defina o preço para venda" type="text" prefix="R$" filled
                    required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Campanhas por Push -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_marketing_notificacao_push" :items="planos"
                    label="Plano - Campanhas por Push" hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_marketing_notificacao_push"
                    label="Preço - Campanhas por Push" hint="Defina o preço para venda" type="text" prefix="R$" filled
                    required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Chat -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_chat" :items="planos" label="Plano - Chat"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_chat" label="Preço - Chat" hint="Defina o preço para venda"
                    type="text" prefix="R$" filled required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Gerente Dedicado -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_gerente_dedicado" :items="planos"
                    label="Plano - Gerente Dedicado" hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_gerente_dedicado" label="Preço - Gerente Dedicado"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Suporte Dedicado -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_suporte_dedicado" :items="planos"
                    label="Plano - Suporte Dedicado" hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_suporte_dedicado" label="Preço - Suporte Dedicado"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Domínio Próprio -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_dominio_proprio" :items="planos" label="Plano - Domínio Próprio"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_dominio_proprio" label="Preço - Domínio Próprio"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Emissor Fiscal (NFC-e) -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_fiscal" :items="planos" label="Plano - Emissor Fiscal (NFC-e)"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_fiscal" label="Preço - Emissor Fiscal (NFC-e)"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Emissor Fiscal (NF-e) -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_fiscal_nfe" :items="planos" label="Plano - Emissor Fiscal (NF-e)"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_fiscal_nfe" label="Preço - Emissor Fiscal (NF-e)"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Distribuição NF-e (DFe) -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_dfe" :items="planos" label="Plano - Distribuição NF-e (DFe)"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_dfe" label="Preço - Distribuição NF-e (DFe)"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Recebimento NFS-e -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_nfse" :items="planos" label="Plano - Recebimento NFS-e"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_nfse" label="Preço - Recebimento NFS-e"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Robô de Atendimento -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_whatsapp_robo_atendimento" :items="planos"
                    label="Plano - Robô de Atendimento" hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_whatsapp_robo_atendimento"
                    label="Preço - Robô de Atendimento" hint="Defina o preço para venda" type="text" prefix="R$" filled
                    required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Ifood -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_ifood" :items="planos" label="Plano - Ifood"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_ifood" label="Preço - Ifood" hint="Defina o preço para venda"
                    type="text" prefix="R$" filled required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Certificado digital -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_certificado_digital" :items="planos" label="Plano - Certificado digital"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_certificado_digital" label="Preço - Certificado digital"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Campanhas agendadas -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_cron" :items="planos" label="Plano - Campanhas agendadas"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_cron" label="Preço - Campanhas agendadas"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Google Meu Negócio -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_google_meu_negocio" :items="planos"
                    label="Plano - Google Meu Negócio" hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_google_meu_negocio" label="Preço - Google Meu Negócio"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Consultoria Estratégica -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_consultoria_estrategica" :items="planos"
                    label="Plano - Consultoria Estratégica" hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_consultoria_estrategica"
                    label="Preço - Consultoria Estratégica" hint="Defina o preço para venda" type="text" prefix="R$"
                    filled required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- Consultoria iFood -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_consultoria_ifood" :items="planos"
                    label="Plano - Consultoria iFood" hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_consultoria_ifood" label="Preço - Consultoria iFood"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- G-TOTEM -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_totem" :items="planos" label="Plano - G-TOTEM"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_totem" label="Preço - G-TOTEM"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- G-GESTOR -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_gestor" :items="planos" label="Plano - G-GESTOR"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_gestor" label="Preço - G-GESTOR"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- G-POS -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_gpos" :items="planos" label="Plano - G-POS"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_gpos" label="Preço - G-POS" hint="Defina o preço para venda"
                    type="text" prefix="R$" filled required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- G-MESAS -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_gmesas" :items="planos" label="Plano - G-MESAS"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_gmesas" label="Preço - G-POS"
                    hint="Defina o preço para venda" type="text" prefix="R$" filled required
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- G-TEF -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_gtef" :items="planos" label="Plano - G-TEF"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_gtef" label="Preço - G-TEF" hint="Defina o preço para venda"
                    type="text" prefix="R$" filled required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <!-- G-ZAP -->
              <v-row class="mb-2">
                <v-col cols="12" sm="6">
                  <v-select v-model="inputs.plano_modulo_gzap" :items="planos" label="Plano - G-ZAP"
                    hint="Selecione o plano" filled persistent-hint></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="inputs.preco_modulo_gzap" label="Preço - G-ZAP" hint="Defina o preço para venda"
                    type="text" prefix="R$" filled required persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <h3 class="mt-3 blue--text">Interno</h3>
              <hr class="mb-3 mt-2" />

              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field class="mb-2" v-model="inputs.site" label="Site" hint="Site da empresa sem http:// ou www."
                    type="url" filled required readonly persistent-hint></v-text-field>

                  <v-text-field class="mb-2" v-model="inputs.logo" label="Identificação da logo"
                    hint="Identificação interna da logomarca" type="text" filled required readonly
                    persistent-hint></v-text-field>

                  <v-text-field class="mb-2" v-model="inputs.asaas_id_revenda" label="Identificação Asaas"
                    hint="Identificação interna para cobranças das faturas" type="text" filled required readonly
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>

              <h3 class="mt-3 blue--text">Segurança</h3>
              <hr class="mb-3 mt-2" />

              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field class="mb-2" v-model="inputs.pin" label="PIN"
                    hint="Permita acesso total ao painel apenas com o PIN" type="text" filled
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialogAjustes = false">
              Cancelar
            </v-btn>
            <v-btn color="blue darken-1" text @click="salvarResponsavel()">
              Salvar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo Instalações -->
      <v-dialog v-model="dialogInstalacoes" max-width="1024px">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-laptop</v-icon>
            <span class="headline">Instalações</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="carregarInstalacoes()" class="mr-2">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn icon dark @click="dialogInstalacoes = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text style="max-height: 70vh; overflow-y: auto;">
            <v-container fluid class="py-4">
              <!-- Cards de totalizadores -->
              <v-row class="mb-4">
                <v-col cols="12" sm="6" md="4">
                  <v-card color="primary" dark elevation="2">
                    <v-card-text class="text-center">
                      <div class="text-h3 font-weight-bold">{{ totalLojas }}</div>
                      <div class="text-subtitle-1">Total de lojas</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-card color="success" dark elevation="2">
                    <v-card-text class="text-center">
                      <div class="text-h3 font-weight-bold">{{ novasLojas }}</div>
                      <div class="text-subtitle-1">Novas lojas (7 dias)</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-card color="error" dark elevation="2">
                    <v-card-text class="text-center">
                      <div class="text-h3 font-weight-bold">{{ lojasRemovidas }}</div>
                      <div class="text-subtitle-1">Lojas removidas</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Filtros -->
              <v-row>
                <v-col cols="12" sm="10" md="6">
                  <v-text-field v-model="filtroPesquisa" label="Pesquisar" placeholder="Buscar"
                    prepend-inner-icon="mdi-magnify" outlined dense clearable hide-details></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model="filtroDataInicio" label="Data início" type="date" outlined dense clearable
                    hide-details></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model="filtroDataFim" label="Data fim" type="date" outlined dense clearable
                    hide-details></v-text-field>
                </v-col>
              </v-row>
            </v-container>

            <v-data-table :headers="headersInstalacoes" :items="instalacoesFiltradas"
              @dblclick:row="($event, { item }) => acessarCliente(item)" class="elevation-1" locale="pt" sort-by="nome">
              <template v-slot:item.data_modificacao="{ item }">
                <span>{{ new Date(item.data_modificacao).toLocaleString('pt-br') }}</span>
              </template>

              <template v-slot:item.data_criacao="{ item }">
                <span>{{ item.data_criacao ? new Date(item.data_criacao).toLocaleString('pt-br') : null }}</span>
              </template>

              <template v-slot:item.dados.analytics.totalPedidos="{ item }">
                {{ item.dados.analytics.totalPedidos | formatNumber }}
              </template>

              <template v-slot:item.dados.analytics.totalFaturamento="{ item }">
                {{ Number(item.dados.analytics.totalFaturamento).toLocaleString("pt-br", {
                  style: "currency",
                  currency: "BRL",
                }) }}
              </template>

            </v-data-table>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialogInstalacoes = false">
              Fechar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo Revendas -->
      <v-dialog v-model="dialogRevendas" max-width="1024px">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-laptop-account</v-icon>
            <span class="headline">Revendas</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="carregarRevendas()" class="mr-2">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn icon dark @click="dialogRevendas = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text style="max-height: 70vh; overflow-y: auto;">
            <v-container fluid class="py-4">
              <!-- Cards de totalizadores -->
              <v-row class="mb-4">
                <v-col cols="12" sm="6" md="3">
                  <v-card color="primary" dark elevation="2">
                    <v-card-text class="text-center">
                      <div class="text-h3 font-weight-bold">{{ totalRevendas }}</div>
                      <div class="text-subtitle-1">Total de revendas</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-card color="success" dark elevation="2">
                    <v-card-text class="text-center">
                      <div class="text-h3 font-weight-bold">{{ novasRevendas }}</div>
                      <div class="text-subtitle-1">Novas revendas (7 dias)</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-card color="error" dark elevation="2">
                    <v-card-text class="text-center">
                      <div class="text-h3 font-weight-bold">{{ revendasRemovidas }}</div>
                      <div class="text-subtitle-1">Revendas removidas</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-card color="info" dark elevation="2">
                    <v-card-text class="text-center">
                      <div class="text-h3 font-weight-bold">{{ Number(totalCreditosRevendas).toLocaleString("pt-br", {
                        style: "currency", currency: "BRL"
                      }) }}</div>
                      <div class="text-subtitle-1">Total em créditos</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Filtros -->
              <v-row>
                <v-col cols="12" sm="10" md="6">
                  <v-text-field v-model="filtroPesquisaRevenda" label="Pesquisar" placeholder="Buscar"
                    prepend-inner-icon="mdi-magnify" outlined dense clearable hide-details></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model="filtroDataInicioRevenda" label="Data início" type="date" outlined dense clearable
                    hide-details></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model="filtroDataFimRevenda" label="Data fim" type="date" outlined dense clearable
                    hide-details></v-text-field>
                </v-col>
              </v-row>
            </v-container>

            <v-data-table :headers="headersRevendas" :items="revendasFiltradas"
              @dblclick:row="($event, { item }) => acessarRevenda(item)" class="elevation-1" locale="pt" sort-by="nome">
              <template v-slot:item.data_modificacao="{ item }">
                <span>{{ new Date(item.data_modificacao).toLocaleString('pt-br') }}</span>
              </template>

              <template v-slot:item.data_criacao="{ item }">
                <span>{{ item.data_criacao ? new Date(item.data_criacao).toLocaleString('pt-br') : null }}</span>
              </template>

              <template v-slot:item.creditos="{ item }">
                {{ Number(item.creditos).toLocaleString("pt-br", {
                  style: "currency",
                  currency: "BRL",
                }) }}
              </template>

            </v-data-table>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialogRevendas = false">
              Fechar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo Transações -->
      <v-dialog v-model="dialogTransacoes" max-width="1024px">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-bank-transfer</v-icon>
            <span class="headline">Transações de créditos</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="carregarTransacoes()" class="mr-2">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn icon dark @click="dialogTransacoes = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text style="max-height: 70vh; overflow-y: auto;" class="pa-0">
            <v-data-table :headers="headersTransacoes" :items="transacoes"
              @dblclick:row="($event, { item }) => acessarRevenda(item)" class="elevation-1" locale="pt"
              sort-by="data_criacao" sort-desc>
              <template v-slot:item.data_criacao="{ item }">
                <span>{{ new Date(item.data_criacao).toLocaleString('pt-br') }}</span>
              </template>

              <template v-slot:item.data_modificacao="{ item }">
                <span>{{ new Date(item.data_modificacao).toLocaleString('pt-br') }}</span>
              </template>

              <template v-slot:item.creditos="{ item }">
                {{ Number(item.creditos).toLocaleString("pt-br", {
                  style: "currency",
                  currency: "BRL",
                }) }}
              </template>

              <template v-slot:item.valor="{ item }">
                {{ Number(item.valor).toLocaleString("pt-br", {
                  style: "currency",
                  currency: "BRL",
                }) }}
              </template>
            </v-data-table>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialogTransacoes = false">
              Fechar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo Desbloquear IP -->
      <v-dialog v-model="dialogDesbloquearIp" max-width="500px" persistent>
        <v-card>
          <v-card-title class="blue darken-2 white--text">
            <v-icon left color="white">mdi-lock-open-outline</v-icon>
            <span class="headline">Desbloquear IP</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogDesbloquearIp = false" :disabled="desbloqueandoIp">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-4">
            <p class="mt-2">Informe o IP que deseja desbloquear.</p>
            <v-text-field v-model="ipDesbloquear" label="IP" placeholder="Ex.: 192.168.0.1" outlined dense
              :disabled="desbloqueandoIp" @keyup.enter="confirmarDesbloqueioIp()"></v-text-field>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialogDesbloquearIp = false" :disabled="desbloqueandoIp">
              Cancelar
            </v-btn>
            <v-btn color="blue darken-2" dark @click="confirmarDesbloqueioIp()" :loading="desbloqueandoIp"
              :disabled="!ipDesbloquear || desbloqueandoIp">
              Desbloquear
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo Instâncias WhatsApp -->
      <v-dialog v-model="dialogInstanciasWhatsApp" max-width="1400px">
        <v-card>
          <v-card-title class="success white--text">
            <v-icon left color="white">mdi-whatsapp</v-icon>
            <span class="headline">Instâncias</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="carregarInstanciasWhatsApp()" class="mr-2">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn icon dark @click="dialogInstanciasWhatsApp = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text style="max-height: 70vh; overflow-y: auto;">
            <v-container fluid class="py-4">
              <!-- Cards de totalizadores -->
              <v-row class="mb-4">
                <v-col cols="12" sm="6" md="4">
                  <v-card color="primary" dark elevation="2">
                    <v-card-text class="text-center">
                      <div class="text-h3 font-weight-bold">{{ totalInstancias }}</div>
                      <div class="text-subtitle-1">Total de instâncias</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-card color="success" dark elevation="2">
                    <v-card-text class="text-center">
                      <div class="text-h3 font-weight-bold">{{ instanciasConectadasHoje }}</div>
                      <div class="text-subtitle-1">Conectadas hoje</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-card color="info" dark elevation="2">
                    <v-card-text class="text-center">
                      <div class="text-h3 font-weight-bold">{{ robotsAtivos }}</div>
                      <div class="text-subtitle-1">Robôs ativos</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Métricas dos servidores -->
              <v-row class="mb-2">
                <v-col cols="6" sm="4" md="2">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <div class="text-h5 font-weight-bold">{{ metricasAgregadas.messagesSent }}</div>
                      <div class="caption grey--text">Mensagens enviadas</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="2">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <div class="text-h5 font-weight-bold">{{ metricasAgregadas.messagesReceived }}</div>
                      <div class="caption grey--text">Mensagens recebidas</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="2">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <div class="text-h5 font-weight-bold">{{ metricasAgregadas.webhooksSent }}</div>
                      <div class="caption grey--text">Webhooks enviados</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="2">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <div class="text-h5 font-weight-bold"
                        :class="{ 'error--text': metricasAgregadas.webhooksError > 0 }">
                        {{ metricasAgregadas.webhooksError }}
                      </div>
                      <div class="caption grey--text">Webhooks com erro</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="2">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <div class="text-h5 font-weight-bold" :class="{ 'error--text': metricasAgregadas.sendErrors > 0 }">
                        {{ metricasAgregadas.sendErrors }}
                      </div>
                      <div class="caption grey--text">Erros de envio</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="2">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <div class="text-h5 font-weight-bold" :class="{ 'error--text': errosGamazap.length > 0 }">
                        {{ errosGamazap.length }}
                      </div>
                      <div class="caption grey--text">Erros recentes</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Filtro de pesquisa -->
              <v-row class="mb-3">
                <v-col cols="12" sm="8" md="6">
                  <v-text-field v-model="pesquisaInstancia" label="Pesquisar" placeholder="Buscar"
                    prepend-inner-icon="mdi-magnify" outlined dense clearable hide-details></v-text-field>
                </v-col>
              </v-row>
            </v-container>

            <v-data-table :headers="headersInstanciasWhatsApp" :items="instanciasFiltradas" class="elevation-1"
              locale="pt" sort-by="instance_key" @dblclick:row="($event, { item }) => acessarInstancia(item)">
              <template v-slot:item.data_connected="{ item }">
                <span>{{ item.data_connected ? new Date(item.data_connected).toLocaleString('pt-br') : '-' }}</span>
              </template>

              <template v-slot:item.user.id="{ item }">
                <span>{{ item.user.id | formatPhone }}</span>
              </template>

              <template v-slot:item.phone_connected="{ item }">
                <v-chip :color="item.phone_connected ? 'success' : 'error'" small dark>
                  {{ item.phone_connected ? 'Conectado' : 'Desconectado' }}
                </v-chip>
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip :color="item.status === 'open' ? 'success' : 'warning'" small dark>
                  {{ item.status }}
                </v-chip>
              </template>

              <template v-slot:item.webhook.enabled="{ item }">
                <v-icon :color="item.webhook.enabled ? 'success' : 'grey'">
                  {{ item.webhook.enabled ? 'mdi-check-circle' : 'mdi-close-circle' }}
                </v-icon>
              </template>

              <template v-slot:item.acoes="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon color="error" v-bind="attrs" v-on="on" @click="confirmarRemocaoInstancia(item)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Remover instância</span>
                </v-tooltip>
              </template>
            </v-data-table>

            <!-- Últimos erros -->
            <div class="d-flex align-center mt-6 mb-2">
              <v-icon left color="error">mdi-alert-circle-outline</v-icon>
              <span class="text-h6">Últimos erros</span>
              <v-chip class="ml-2" small :color="errosGamazap.length ? 'error' : 'success'" dark>
                {{ errosGamazap.length }}
              </v-chip>
            </div>

            <v-data-table :headers="headersErrosGamazap" :items="errosGamazap" class="elevation-1" locale="pt"
              :items-per-page="10" no-data-text="Nenhum erro registrado">
              <template v-slot:item.at="{ item }">
                <span>{{ item.at ? new Date(item.at).toLocaleString('pt-br') : '-' }}</span>
              </template>

              <template v-slot:item.type="{ item }">
                <v-chip color="warning" small dark>{{ item.type }}</v-chip>
              </template>

              <template v-slot:item.error="{ item }">
                <span class="error--text">{{ item.error }}</span>
              </template>
            </v-data-table>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialogInstanciasWhatsApp = false">
              Fechar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo Confirmar Remoção Instância -->
      <v-dialog v-model="dialogConfirmarRemocao" max-width="500px">
        <v-card>
          <v-card-title class="error white--text">
            <v-icon left color="white">mdi-alert</v-icon>
            <span class="headline">Confirmar remoção</span>
          </v-card-title>

          <v-card-text class="pt-4">
            <p class="text-h6">Tem certeza que deseja remover {{ instanciaParaRemover ? instanciaParaRemover.instance_key
              : 'a conexão' }}?</p>
            <p class="mt-3 error--text font-weight-bold">Esta ação não pode ser desfeita!</p>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="dialogConfirmarRemocao = false">
              Cancelar
            </v-btn>
            <v-btn color="error" text @click="removerInstanciaWhatsApp()" :loading="removendoInstancia">
              Remover
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo Créditos -->
      <v-dialog v-model="dialogCreditos" max-width="600px">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-bank</v-icon>
            <span class="headline">Créditos</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogCreditos = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text style="max-height: 70vh; overflow-y: auto;">
            <v-container>
              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field label="Empresa" :value="revenda.nome" filled readonly persistent-hint></v-text-field>
                  <v-text-field label="Saldo" :value="revenda.creditos" type="number" prefix="R$" filled readonly
                    persistent-hint></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialogCreditos = false">
              Fechar
            </v-btn>
            <v-btn color="success darken-1" text @click="abrirAdicionarCreditos()">
              Adicionar créditos
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo Adicionar Créditos -->
      <v-dialog v-model="dialogAddCreditos" max-width="600px">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-bank</v-icon>
            <span class="headline">Adicionar créditos</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogAddCreditos = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text style="max-height: 70vh; overflow-y: auto;">
            <v-container>
              <v-alert type="info" text dense border="left" class="mb-4">
                Os créditos serão aplicados automaticamente na próxima fatura. Se a fatura já foi gerada, eles entrarão
                apenas na fatura seguinte.
              </v-alert>
              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field ref="campoCreditos" label="Créditos" v-model="inputsCreditos.creditos" value="0,00"
                    type="number" min="5" max="100000" prefix="R$" filled persistent-hint></v-text-field>
                </v-col>
              </v-row>
              <v-row v-if="Number(inputsCreditos.creditos) > 0">
                <v-col cols="12" sm="12" md="12">
                  <v-card outlined class="pa-3">
                    <div class="d-flex justify-space-between align-center">
                      <span class="grey--text text--darken-1">Você vai pagar</span>
                      <span class="text-h6 success--text font-weight-bold">{{ valorPagarCreditos }}</span>
                    </div>
                    <div class="d-flex justify-space-between align-center mt-2">
                      <span class="grey--text text--darken-1">Você vai receber</span>
                      <span class="text-h6 font-weight-bold">{{ valorReceberCreditos }}</span>
                    </div>
                    <div class="caption grey--text mt-2">
                      Pagamento com 10% de desconto aplicado no pix.
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialogAddCreditos = false">
              Fechar
            </v-btn>
            <v-btn color="success darken-1" text @click="adicionarCreditos()">
              Adicionar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo Tabela de Valores/Preços -->
      <v-dialog v-model="dialogTabelaValores" max-width="600px">
        <v-card>
          <v-card-title class="success white--text">
            <v-icon left color="white">mdi-list-box-outline</v-icon>
            <span class="headline">Tabela de valores</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogTabelaValores = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text style="max-height: 70vh; overflow-y: auto;">
            <v-container>
              <p>Todos os outros modulos nao listados podem ser utilizados de forma gratuita e voce pode cobrar o valor
                que preferir por cada um deles.</p>

              <h3 class="mt-3 blue--text">Valores</h3>
              <hr class="mb-3 mt-2" />

              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorMinimoRevenda" label="Valor minimo"
                    :hint="'Valor minimo cobrado mensalmente (' + this.qtdMinimoLojasRevenda + ' lojas)'" type="number"
                    prefix="R$" filled readonly persistent-hint></v-text-field>
                </v-col>

                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorRevenda" label="Fatura minima"
                    :hint="'Valor minimo de fatura (mesmo utilizando creditos)'" type="number" prefix="R$" filled readonly
                    persistent-hint></v-text-field>
                </v-col>

                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorRevenda" label="Loja"
                    hint="Valor cobrado mes/por loja (exceto demonstracao)" type="number" prefix="R$" filled readonly
                    persistent-hint></v-text-field>
                </v-col>

                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorGamaZapCampanhas" label="WhatsApp - Campanhas" hint="mes/por loja"
                    type="number" prefix="R$" filled readonly persistent-hint></v-text-field>
                </v-col>

                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorRobo" label="WhatsApp - Robo de atendimento" hint="mes/por loja"
                    type="number" prefix="R$" filled readonly persistent-hint></v-text-field>
                </v-col>

                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorFiscal" label="Emissao de NFC-e/NF-e/DF-e/NFS-e" hint="mes/por loja"
                    type="number" prefix="R$" filled readonly persistent-hint></v-text-field>
                </v-col>

                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorGtef" label="G-TEF" hint="por POS ativa" type="number" prefix="R$"
                    filled readonly persistent-hint></v-text-field>
                </v-col>

                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorGtefSitef" label="G-TEF SiTef" hint="por terminal ativo" type="number"
                    prefix="R$" filled readonly persistent-hint></v-text-field>
                </v-col>

                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorGtefScope" label="G-TEF Scope" hint="por terminal ativo" type="number"
                    prefix="R$" filled readonly persistent-hint></v-text-field>
                </v-col>

                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorGpos" label="G-POS" hint="por loja ativa" type="number" prefix="R$"
                    filled readonly persistent-hint></v-text-field>
                </v-col>

                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorGmesas" label="G-MESAS" hint="por loja ativa" type="number" prefix="R$"
                    filled readonly persistent-hint></v-text-field>
                </v-col>

                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorGtotem" label="G-TOTEM" hint="por loja ativa" type="number" prefix="R$"
                    filled readonly persistent-hint></v-text-field>
                </v-col>

                <v-col cols="12" sm="12" md="12">
                  <v-text-field v-model="this.valorSuporte" label="Suporte ao cliente" hint="por loja ativa" type="number"
                    prefix="R$" filled readonly persistent-hint></v-text-field>
                </v-col>

              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialogTabelaValores = false">
              Fechar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { encerrar } from '../../utils/utils'
import { verificarInstanciaGamazap, iniciarInstanciaGamazap, responsavelGeralDados, responsavelGeralSalvar, listarInstalacoes, listarRevendas, listarTransacoes, validarRevenda, asaasCriarFatura, listarInstancias, removerInstancia, desbloquearIp } from '../../services/api'

export default {
  name: 'Sidebar',
  data() {
    return {
      drawer: true,
      mini: false,
      dialogWhatsApp: false,
      dialogAsaas: false,
      dialogAjustes: false,
      dialogInstalacoes: false,
      dialogRevendas: false,
      dialogTransacoes: false,
      dialogCreditos: false,
      dialogAddCreditos: false,
      dialogInstanciasWhatsApp: false,
      dialogConfirmarRemocao: false,
      dialogTabelaValores: false,
      dialogSair: false,
      dialogDesbloquearIp: false,
      ipDesbloquear: '',
      desbloqueandoIp: false,
      qrcode: '',
      webhookUrl: '',
      token: localStorage.getItem("token"),
      inputs: {},
      inputsCreditos: {},
      instalacoes: [],
      filtroDataInicio: null,
      filtroDataFim: null,
      filtroPesquisa: '',
      revendas: [],
      filtroPesquisaRevenda: '',
      filtroDataInicioRevenda: null,
      filtroDataFimRevenda: null,
      transacoes: [],
      instanciasWhatsApp: [],
      metricasGamazap: [],
      pesquisaInstancia: '',
      removendoInstancia: false,
      instanciaParaRemover: null,
      servidoresGamazap: [
        'http://gamazap6.gamadelivery.app',
        'http://gamazap5.gamadelivery.app',
        'http://gamazap4.gamadelivery.app',
        'http://gamazap3.gamadelivery.app',
        'http://gamazap2.gamadelivery.app',
        'http://gamazap.gamadelivery.app'
      ],
      revenda: {
        creditos: 0.00,
        nome: '',
      },
      mostrarCreditos: false,
      error: {
        show: false,
        title: "",
        message: "",
      },
      headersInstalacoes: [
        {
          text: "UF",
          align: "start",
          value: "dados.endereco.uf",
        },
        {
          text: "Cidade",
          value: "dados.endereco.cidade",
        },
        {
          text: "Empresa",
          value: "dados.empresa.nome",
        },
        {
          text: "Loja",
          value: "nome",
        },
        {
          text: "Versão",
          value: "dados.instalacao.versao",
        },
        { text: "Pedidos", value: "dados.analytics.totalPedidos" },
        { text: "Faturamento", value: "dados.analytics.totalFaturamento" },
        { text: "Última verificação", value: "data_modificacao" },
        { text: "Cadastrado em", value: "data_criacao" },
      ],
      headersRevendas: [
        { text: "Revenda", value: "nome" },
        { text: "Créditos", value: "creditos" },
        { text: "Última verificação", value: "data_modificacao" },
        { text: "Cadastrado em", value: "data_criacao" },
      ],
      headersTransacoes: [
        { text: "Revenda", value: "revenda" },
        { text: "ID Transação", value: "id_transacao" },
        { text: "Valor", value: "valor" },
        { text: "Créditos", value: "creditos" },
        { text: "Atualização", value: "data_modificacao" },
        { text: "Data", value: "data_criacao" },
      ],
      headersInstanciasWhatsApp: [
        { text: "Servidor", value: "servidor" },
        { text: "Loja", value: "instance_key" },
        { text: "Nome", value: "user.name" },
        { text: "Telefone", value: "user.id" },
        { text: "Status", value: "phone_connected" },
        { text: "Conectado em", value: "data_connected" },
        { text: "Robô", value: "webhook.enabled" },
        { text: "Ações", value: "acoes", sortable: false, align: "center" },
      ],
      headersErrosGamazap: [
        { text: "Quando", value: "at" },
        { text: "Servidor", value: "servidor" },
        { text: "Tipo", value: "type" },
        { text: "Loja", value: "instanceKey" },
        { text: "Erro", value: "error" },
      ]
    }
  },
  computed: {
    valorReceberCreditos() {
      const creditos = Number(this.inputsCreditos.creditos) || 0;
      return creditos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    valorPagarCreditos() {
      const creditos = Number(this.inputsCreditos.creditos) || 0;
      return (creditos - (creditos * 0.10)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    isTicketsActive() {
      return this.$route.path.includes('/tickets');
    },
    isFinanceiroActive() {
      return this.$route.path.includes('/faturas');
    },
    isFaturamentoActive() {
      return this.$route.path.includes('/faturamento');
    },
    instalacoesFiltradas() {
      let resultado = this.instalacoes;

      if (this.filtroDataInicio || this.filtroDataFim) {
        resultado = resultado.filter(instalacao => {
          const dataCriacao = new Date(instalacao.data_criacao);
          const inicio = this.filtroDataInicio ? new Date(this.filtroDataInicio) : null;
          const fim = this.filtroDataFim ? new Date(this.filtroDataFim) : null;

          if (inicio && fim) {
            return dataCriacao >= inicio && dataCriacao <= fim;
          } else if (inicio) {
            return dataCriacao >= inicio;
          } else if (fim) {
            return dataCriacao <= fim;
          }
          return true;
        });
      }

      if (this.filtroPesquisa) {
        const termo = this.filtroPesquisa.toLowerCase();
        resultado = resultado.filter(instalacao => {
          return (
            instalacao.nome?.toLowerCase().includes(termo) ||
            instalacao.dados?.empresa?.nome?.toLowerCase().includes(termo) ||
            instalacao.dados?.endereco?.cidade?.toLowerCase().includes(termo) ||
            instalacao.dados?.endereco?.uf?.toLowerCase().includes(termo)
          );
        });
      }

      return resultado;
    },
    totalLojas() {
      return this.instalacoesFiltradas.filter(instalacao => {
        return instalacao.data_criacao && instalacao.data_criacao.length > 0;
      }).length;
    },
    novasLojas() {
      const seteDiasAtras = new Date();
      seteDiasAtras.setDate(seteDiasAtras.getDate() - 7);

      return this.instalacoesFiltradas.filter(instalacao => {
        const dataCriacao = new Date(instalacao.data_criacao);
        return dataCriacao >= seteDiasAtras;
      }).length;
    },
    lojasRemovidas() {
      return this.instalacoesFiltradas.filter(instalacao => {
        return !instalacao.data_criacao || instalacao.data_criacao === '' || instalacao.data_criacao === null;
      }).length;
    },
    revendasFiltradas() {
      let resultado = this.revendas;

      if (this.filtroDataInicioRevenda || this.filtroDataFimRevenda) {
        resultado = resultado.filter(revenda => {
          const dataCriacao = new Date(revenda.data_criacao);
          const inicio = this.filtroDataInicioRevenda ? new Date(this.filtroDataInicioRevenda) : null;
          const fim = this.filtroDataFimRevenda ? new Date(this.filtroDataFimRevenda) : null;

          if (inicio && fim) {
            return dataCriacao >= inicio && dataCriacao <= fim;
          } else if (inicio) {
            return dataCriacao >= inicio;
          } else if (fim) {
            return dataCriacao <= fim;
          }
          return true;
        });
      }

      if (this.filtroPesquisaRevenda) {
        const termo = this.filtroPesquisaRevenda.toLowerCase();
        resultado = resultado.filter(revenda => {
          return (
            revenda.nome?.toLowerCase().includes(termo) ||
            revenda.url?.toLowerCase().includes(termo)
          );
        });
      }

      return resultado;
    },
    totalRevendas() {
      return this.revendasFiltradas.length;
    },
    novasRevendas() {
      const seteDiasAtras = new Date();
      seteDiasAtras.setDate(seteDiasAtras.getDate() - 7);

      return this.revendasFiltradas.filter(revenda => {
        const dataCriacao = new Date(revenda.data_criacao);
        return dataCriacao >= seteDiasAtras;
      }).length;
    },
    totalCreditosRevendas() {
      return this.revendasFiltradas.reduce((total, revenda) => {
        return total + (Number(revenda.creditos) || 0);
      }, 0);
    },
    revendasRemovidas() {
      return this.revendasFiltradas.filter(revenda => {
        return !revenda.data_criacao || revenda.data_criacao === '' || revenda.data_criacao === null;
      }).length;
    },
    instanciasFiltradas() {
      if (!this.pesquisaInstancia) {
        return this.instanciasWhatsApp;
      }

      const termo = this.pesquisaInstancia.toLowerCase();
      return this.instanciasWhatsApp.filter(instancia => {
        return (
          instancia.instance_key?.toLowerCase().includes(termo) ||
          instancia.user?.name?.toLowerCase().includes(termo) ||
          instancia.user?.id?.toLowerCase().includes(termo) ||
          instancia.servidor?.toLowerCase().includes(termo)
        );
      });
    },
    totalInstancias() {
      return this.instanciasFiltradas.length;
    },
    instanciasConectadasHoje() {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      return this.instanciasFiltradas.filter(instancia => {
        if (!instancia.data_connected) return false;
        const dataConexao = new Date(instancia.data_connected);
        dataConexao.setHours(0, 0, 0, 0);
        return dataConexao.getTime() === hoje.getTime();
      }).length;
    },
    robotsAtivos() {
      return this.instanciasFiltradas.filter(instancia => {
        return instancia.webhook?.enabled === true;
      }).length;
    },
    metricasAgregadas() {
      return this.metricasGamazap.reduce((total, m) => {
        total.messagesSent += m.messagesSent || 0;
        total.messagesReceived += m.messagesReceived || 0;
        total.webhooksSent += m.webhooksSent || 0;
        total.webhooksError += m.webhooksError || 0;
        total.sendErrors += m.sendErrors || 0;
        return total;
      }, {
        messagesSent: 0,
        messagesReceived: 0,
        webhooksSent: 0,
        webhooksError: 0,
        sendErrors: 0,
      });
    },
    errosGamazap() {
      return this.metricasGamazap
        .flatMap(m => [
          ...(m.failedWebhooks || []),
          ...(m.failedMessages || []),
        ].map(erro => ({ ...erro, servidor: m.servidor })))
        .sort((a, b) => new Date(b.at) - new Date(a.at));
    }
  },
  methods: {
    setError(error, title) {
      console.error(title, error);
      this.error = {
        show: true,
        title: title ? title : "Ops, falhou!",
        message: error ? error : "Falha não identificada, tente novamente.",
      };
    },
    irParaGuia() {
      window.open(`https://meajuda.gitbook.io/guia-rapido`, "_blank");
    },
    irParaApoio() {
      window.open(`https://meajuda.gitbook.io/guia-do-parceiro`, "_blank");
    },
    irParaApoioSuporte() {
      window.open(`https://meajuda.gitbook.io/apoio-suporte`, "_blank");
    },
    irParaYoutube() {
      window.open(`https://vimeo.com/reviews/39174ee8-4a5b-45ae-9fe2-d26e29ed7847/users/221467278/folders/21233154`, "_blank");
    },
    irParaAjuda() {
      window.open(`https://ajuda${this.baseUrl}`, "_blank");
    },
    irParaStatus() {
      window.open(`https://status.gamadelivery.app`, "_blank");
    },
    irParaSugestoes() {
      window.open(`https://feedback.gamadelivery.app`, "_blank");
    },
    irParaDoc() {
      window.open(`https://api.gamadelivery.app`, "_blank");
    },
    irParaComunidade() {
      window.open(`https://chat.whatsapp.com/BXNoLXR8ltN34KXeRA7XDF`, "_blank");
    },
    baixar(url) {
      window.open(url, "_blank");
    },
    sair() {
      this.dialogSair = true;
    },
    confirmarSair() {
      this.dialogSair = false;
      return encerrar();
    },
    irParaFaturas() {
      const asaasIdRevenda = localStorage.getItem('asaas_id_revenda') || '';
      if (asaasIdRevenda) {
        const targetPath = `/faturas/${asaasIdRevenda}`;
        if (this.$route.path !== targetPath) {
          this.$router.push({ path: targetPath });
        }
      } else {
        this.setError('Ainda não disponível para sua conta, tente novamente mais tarde.');
      }
    },
    abrirTabelaValores() {
      this.dialogTabelaValores = true;
    },
    abrirAjustes() {
      this.carregarResponsavel();
    },
    carregarResponsavel() {
      this.inputs = {};
      this.$root.$emit('global-loading', true);
      responsavelGeralDados(this.token)
        .then((response) => {
          this.$root.$emit('global-loading', false);
          this.inputs = response.data?.responsavel;
          this.dialogAjustes = true;
        })
        .catch((error) => {
          this.$root.$emit('global-loading', false);
          this.setError(error?.response?.data?.message || 'Erro ao carregar dados');
        });
    },
    salvarResponsavel() {
      this.dialogAjustes = false;
      this.$root.$emit('global-loading', true);
      responsavelGeralSalvar(this.token, this.inputs)
        .then(() => {
          this.$root.$emit('global-loading', false);
        })
        .catch((error) => {
          this.dialogAjustes = true;
          this.$root.$emit('global-loading', false);
          this.setError(error?.response?.data?.message || 'Erro ao salvar');
        });
    },
    verificarWhatsApp() {
      this.$root.$emit('global-loading', true);
      verificarInstanciaGamazap(this.token)
        .then(() => {
          this.$root.$emit('global-loading', false);
          this.setError("O seu WhatsApp está conectado!", "Conectado!");
        })
        .catch(() => {
          this.conectarWhatsApp();
        });
    },
    conectarWhatsApp() {
      iniciarInstanciaGamazap(this.token)
        .then((response) => {
          this.$root.$emit('global-loading', false);
          this.qrcode = response?.data?.qrcode?.base64;
          if (this.qrcode) {
            this.dialogWhatsApp = true;
            setTimeout(() => this.dialogWhatsApp = false, 60000);
          }
        })
        .catch((error) => {
          this.dialogWhatsApp = true;
          this.$root.$emit('global-loading', false);
          this.setError(error?.response?.data?.message || 'Erro ao conectar WhatsApp');
        });
    },
    carregarAsaas() {
      const apiKey = localStorage.getItem('api_key') || '';
      this.webhookUrl = `${window.location.origin}/api_asaas.php?apiKey=${apiKey}`;
      this.dialogAsaas = true;
    },
    limparFiltrosInstalacoes() {
      this.filtroDataInicio = null;
      this.filtroDataFim = null;
      this.filtroPesquisa = '';
    },
    limparFiltrosRevendas() {
      this.filtroDataInicioRevenda = null;
      this.filtroDataFimRevenda = null;
      this.filtroPesquisaRevenda = '';
    },
    carregarInstalacoes() {
      this.limparFiltrosInstalacoes();
      this.$root.$emit('global-loading', true);
      listarInstalacoes(this.token)
        .then((response) => {
          this.$root.$emit('global-loading', false);
          let lista = [];
          response.data.map((instalacao) => {
            instalacao.dados = JSON.parse(instalacao.dados);
            lista.push(instalacao);
          })
          this.instalacoes = lista;
          this.dialogInstalacoes = true;
        })
        .catch((error) => {
          this.$root.$emit('global-loading', false);
          this.setError(error?.response?.data?.message || 'Erro ao carregar instalações');
        });
    },
    carregarRevendas() {
      this.limparFiltrosRevendas();
      this.$root.$emit('global-loading', true);
      listarRevendas(this.token)
        .then((response) => {
          this.$root.$emit('global-loading', false);
          this.revendas = response.data;
          this.dialogRevendas = true;
        })
        .catch((error) => {
          this.$root.$emit('global-loading', false);
          this.setError(error?.response?.data?.message || 'Erro ao carregar revendas');
        });
    },
    carregarTransacoes() {
      this.$root.$emit('global-loading', true);
      listarTransacoes(this.token)
        .then((response) => {
          this.$root.$emit('global-loading', false);
          this.transacoes = response.data;
          this.dialogTransacoes = true;
        })
        .catch((error) => {
          this.$root.$emit('global-loading', false);
          this.setError(error?.response?.data?.message || 'Erro ao carregar transações');
        });
    },
    abrirDesbloquearIp() {
      this.ipDesbloquear = '';
      this.desbloqueandoIp = false;
      this.dialogDesbloquearIp = true;
    },
    confirmarDesbloqueioIp() {
      const ip = (this.ipDesbloquear || '').trim();
      if (!ip || this.desbloqueandoIp) return;
      this.desbloqueandoIp = true;
      this.$root.$emit('global-loading', true);
      desbloquearIp(this.token, ip)
        .then(() => {
          this.$root.$emit('global-loading', false);
          this.desbloqueandoIp = false;
          this.dialogDesbloquearIp = false;
          this.setError(`IP ${ip} enviado para a fila de desbloqueio.`, 'Pronto!');
        })
        .catch((error) => {
          this.$root.$emit('global-loading', false);
          this.desbloqueandoIp = false;
          this.setError(error?.response?.data?.message || 'Erro ao desbloquear IP');
        });
    },
    acessarInstancia(instancia) {
      window.open(`https://${instancia.instance_key}/acesso-adm`, "_blank");
    },
    acessarCliente(cliente) {
      window.open(`https://${cliente.url}/acesso-adm`, "_blank");
    },
    acessarRevenda(revenda) {
      window.open(`https://${revenda.url}/`, "_blank");
    },
    formatarMoeda(valor) {
      return parseFloat(valor || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      });
    },
    async buscarRevenda() {
      const { data: revenda } = await responsavelGeralDados(this.token);
      const { data: dados } = await validarRevenda(this.dominio, revenda.responsavel?.nome_empresa);
      return { ...dados.data, api_key: revenda.responsavel?.api_key };
    },
    carregarRevenda() {
      const asaasIdRevenda = localStorage.getItem('asaas_id_revenda') || '';
      if (!asaasIdRevenda) {
        this.setError('Ainda não disponível para sua conta, tente novamente mais tarde.');
        return;
      }

      if (!this.mostrarCreditos) {
        this.$root.$emit('global-loading', true);
        this.buscarRevenda()
          .then((revenda) => {
            this.revenda = revenda;
            this.$root.$emit('global-loading', false);
            this.mostrarCreditos = true;
            this.dialogCreditos = true;
          })
          .catch((error) => {
            this.setError(error?.message || 'Erro ao carregar créditos');
            this.$root.$emit('global-loading', false);
          });
      } else {
        this.mostrarCreditos = false;
      }
    },
    abrirAdicionarCreditos() {
      this.dialogCreditos = false;
      this.dialogAddCreditos = true;
      this.inputsCreditos = {};
      setTimeout(() => {
        this.$refs.campoCreditos && this.$refs.campoCreditos.focus();
      }, 300);
    },
    adicionarCreditos() {
      this.$root.$emit('global-loading', true);
      this.dialogAddCreditos = false;

      const token = Math.random().toString(36).substring(2);
      const asaasIdRevenda = localStorage.getItem('asaas_id_revenda');

      // Refletir em Faturas.vue
      asaasCriarFatura(this.token, {
        billingType: "PIX",
        interest: { value: 2 },
        fine: { value: 3, type: "PERCENTAGE" },
        customer: asaasIdRevenda,
        dueDate: new Date(),
        value: (this.inputsCreditos.creditos - (this.inputsCreditos.creditos * 0.10)),
        description: `${this.isDesenvolvimento ? '(DEV)' : ''} ${this.inputsCreditos.creditos} créditos`,
        externalReference: token,
        split: [
          {
            walletId: (this.isDesenvolvimento ? "7171bb1c-f2d2-4729-bf08-ec90431ccaf5" : "ec7ac8c2-9e1f-44c9-9d6e-bae9088523ea"),
            percentualValue: 20,
            externalReference: 'Investimentos',
          },
        ],
        callback: {
          successUrl: `https://painel.gamadelivery.app/api_asaas_credito${this.isDesenvolvimento ? '-dev' : ''}.php?customer=${asaasIdRevenda}&url=${this.revenda.url}&token=${token}&apiKey=${this.revenda.api_key}`,
          autoRedirect: true,
        },
      }).then(({ data }) => {
        window.location.href = data.fatura?.invoiceUrl;
      }).catch((error) => {
        this.setError(error.response?.data?.message || 'Erro ao adicionar créditos');
        this.$root.$emit('global-loading', false);
      });
    },
    async carregarInstanciasWhatsApp() {
      this.instanciasWhatsApp = [];
      this.metricasGamazap = [];

      this.$root.$emit('global-loading', true);

      try {
        const nomeServidor = (servidor) => servidor.replace('http://', '').replace('.gamadelivery.app', '');

        const promises = this.servidoresGamazap.map(servidor =>
          listarInstancias(this.token, servidor)
            .then(response => {
              let data = response.data;
              if (data.error === false && data.data?.instances) {
                const instances = data.data.instances.map(instancia => ({
                  ...instancia,
                  servidor: nomeServidor(servidor)
                }));
                const metricas = data.data.metrics
                  ? { ...data.data.metrics, servidor: nomeServidor(servidor) }
                  : null;
                return { instances, metricas };
              }
              return { instances: [], metricas: null };
            })
            .catch(error => {
              console.error(`Erro ao buscar instâncias de ${servidor}:`, error);
              return { instances: [], metricas: null };
            })
        );

        const resultados = await Promise.all(promises);
        this.instanciasWhatsApp = resultados.flatMap(r => r.instances);
        this.metricasGamazap = resultados.map(r => r.metricas).filter(Boolean);
        this.$root.$emit('global-loading', false);
        this.dialogInstanciasWhatsApp = true;
      } catch (error) {
        this.$root.$emit('global-loading', false);
        this.setError(error?.message || 'Erro ao carregar instâncias');
      }
    },
    confirmarRemocaoInstancia(instancia) {
      this.instanciaParaRemover = instancia;
      this.dialogConfirmarRemocao = true;
    },
    removerInstanciaWhatsApp() {
      if (!this.instanciaParaRemover) return;

      this.removendoInstancia = true;

      const servidor = this.servidoresGamazap.find(s =>
        s.replace('http://', '').replace('.gamadelivery.app', '') === this.instanciaParaRemover.servidor
      );

      if (!servidor) {
        this.setError('Servidor não encontrado', 'Erro');
        this.removendoInstancia = false;
        return;
      }

      removerInstancia(this.token, servidor, this.instanciaParaRemover.instance_key).then(async () => {
        this.dialogConfirmarRemocao = false;
        this.instanciaParaRemover = null;
        this.removendoInstancia = false;
        await this.carregarInstanciasWhatsApp();
      }).catch((error) => {
        throw new Error(error?.response?.data?.message || 'Erro ao remover instância.');
      });
    }
  }
}
</script>

<style scoped>
.sidebar-custom {
  border-right: 1px solid #E0E0E0 !important;
}

.v-navigation-drawer {
  z-index: 999;
}

.v-list-item--active {
  background-color: #E3F2FD !important;
  border-left: 4px solid #1976D2;
}

.v-list-item--active .v-list-item__title,
.v-list-item--active .v-icon {
  color: #1976D2 !important;
  font-weight: 600;
}

.v-list-item:hover {
  background-color: #F5F5F5;
}

.v-list-item:hover .v-list-item__title,
.v-list-item:hover .v-icon {
  color: #1976D2 !important;
}

.v-list-group__header .v-list-item__icon {
  margin-right: 16px;
}

.v-list-group__header .v-list-item__icon .v-icon {
  color: #757575 !important;
}

.v-list-group--active>.v-list-group__header .v-list-item__icon .v-icon {
  color: #1976D2 !important;
}

.v-list-group--active>.v-list-group__header .v-list-item__title {
  color: #1976D2 !important;
  font-weight: 600;
}

.credit-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
}

.credit-card:hover {
  border-left-color: #1976D2;
  transform: translateX(2px);
}
</style>
